import React, { useContext, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { toast } from 'react-toastify';
import { Box, Typography, Button } from '@mui/material';
import moment from 'moment';
import DonutChart from '../../../components/DonutChart';
// Components
import CallPerformanceTable from './CallPerformanceTable';
// Context
import useCallPerformanceContext from '../context/useCallPerformanceContext';
// API
import {
  postCallPerformanceChart,
  postCallPerformanceDetailedView,
} from '../../../api/crmDashboard';

const CallPerformanceTab = forwardRef((props, ref) => {
  const {
    filterForm,
    defaultFilterForm,
    pmdsChart,
    fvpChart,
    callRateChart,
    callReachChart,
    callConcentrationChart,
    setPmdsChart,
    setFvpChart,
    setCallRateChart,
    setCallReachChart,
    setCallConcentrationChart,
    handleToggleModal,
  } = useContext(useCallPerformanceContext);

  const [nsmTable, setNsmTable] = useState({ loading: false, nsmData: [] });
  const [rsmTable, setRsmTable] = useState({ loading: false, rsmData: [] });
  const [mprTable, setMprTable] = useState({ loading: false, mprData: [] });

  const handlePlottedMdsChart = async (clear = null) => {
    try {
      let request;
      if (clear === 'reset') {
        request = {
          chart_type: 'plotted_mds',
          team: defaultFilterForm?.team?.value,
          mpr: defaultFilterForm?.rep_id?.value,
          group: defaultFilterForm?.group?.value,
          date_from: moment(defaultFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(defaultFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          chart_type: 'plotted_mds',
          team: filterForm?.team?.value,
          mpr: filterForm?.rep_id?.value,
          group: filterForm?.group?.value,
          date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallPerformanceChart(request);
      const { code } = response.data;
      if (code === '200') {
        setPmdsChart({
          ...pmdsChart,
          chart: true,
          total_percentage: response?.data?.total_percentage,
          md_universe: response?.data?.md_universe,
          tagged_mds: response?.data?.tagged_mds,
          plotted_mds: response?.data?.plotted_mds,
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleFrequencyvsPlottedChart = async (clear = null) => {
    try {
      let request;

      if (clear === 'reset') {
        request = {
          chart_type: 'freq_vs_plotted',
          team: defaultFilterForm?.team?.value,
          mpr: defaultFilterForm?.rep_id?.value,
          group: defaultFilterForm?.group?.value,
          date_from: moment(defaultFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(defaultFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          chart_type: 'freq_vs_plotted',
          team: filterForm?.team?.value,
          mpr: filterForm?.rep_id?.value,
          group: filterForm?.group?.value,
          date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallPerformanceChart(request);
      const { code } = response.data;
      if (code === '200') {
        setFvpChart({
          ...fvpChart,
          total_percentage: response?.data?.total_percentage,
          frequency: response?.data?.frequency,
          plotted: response?.data?.plotted,
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCallRateChart = async (clear = null) => {
    try {
      let request;

      if (clear === 'reset') {
        request = {
          chart_type: 'call_rate',
          team: defaultFilterForm?.team?.value,
          mpr: defaultFilterForm?.rep_id?.value,
          group: defaultFilterForm?.group?.value,
          date_from: moment(defaultFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(defaultFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          chart_type: 'call_rate',
          team: filterForm?.team?.value,
          mpr: filterForm?.rep_id?.value,
          group: filterForm?.group?.value,
          date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallPerformanceChart(request);
      const { code } = response.data;
      if (code === '200') {
        setCallRateChart({
          ...callRateChart,
          total_percentage: response?.data?.data?.total_percentage,
          plotted: response?.data?.data?.plotted,
          actual: response?.data?.data?.actual,
          planned: response?.data?.data?.planned,
          retrieved: response?.data?.data?.retrieved,
          advanced: response?.data?.data?.advanced,
          missed: response?.data?.data?.missed,
          pending: response?.data?.data?.pending,
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCallReachChart = async (clear = null) => {
    try {
      let request;

      if (clear === 'reset') {
        request = {
          chart_type: 'call_reach',
          team: defaultFilterForm?.team?.value,
          mpr: defaultFilterForm?.rep_id?.value,
          group: defaultFilterForm?.group?.value,
          date_from: moment(defaultFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(defaultFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          chart_type: 'call_reach',
          team: filterForm?.team?.value,
          mpr: filterForm?.rep_id?.value,
          group: filterForm?.group?.value,
          date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallPerformanceChart(request);
      const { code } = response.data;
      if (code === '200') {
        setCallReachChart({
          ...callReachChart,
          total_percentage: response?.data?.total_percentage,
          target: response?.data?.target,
          reach: response?.data?.reach,
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCallConcentrationChart = async (clear = null) => {
    try {
      let request;

      if (clear === 'reset') {
        request = {
          chart_type: 'call_concentration',
          team: defaultFilterForm?.team?.value,
          mpr: defaultFilterForm?.rep_id?.value,
          group: defaultFilterForm?.group?.value,
          date_from: moment(defaultFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(defaultFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          chart_type: 'call_concentration',
          team: filterForm?.team?.value,
          mpr: filterForm?.rep_id?.value,
          group: filterForm?.group?.value,
          date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallPerformanceChart(request);
      const { code } = response.data;
      if (code === '200') {
        setCallConcentrationChart({
          ...callConcentrationChart,
          total_percentage: response?.data?.total_percentage,
          target: response?.data?.target,
          concentration: response?.data?.concentration,
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleNsmTable = async (clear = null) => {
    try {
      setNsmTable({ ...nsmTable, loading: true });
      let request;

      if (clear === 'reset') {
        request = {
          table_type: 'nsm',
          team: defaultFilterForm?.team?.value,
          mpr: defaultFilterForm?.rep_id?.value,
          group: defaultFilterForm?.group?.value,
          date_from: moment(defaultFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(defaultFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          table_type: 'nsm',
          team: filterForm?.team?.value,
          mpr: filterForm?.rep_id?.value,
          group: filterForm?.group?.value,
          date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallPerformanceDetailedView(request);
      const { code, data } = response.data;
      if (code === '200') {
        setNsmTable({ ...nsmTable, loading: false, nsmData: data });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRsmTable = async (clear = null) => {
    try {
      setRsmTable({ ...rsmTable, loading: true });

      let request;

      if (clear === 'reset') {
        request = {
          table_type: 'rsm',
          team: defaultFilterForm?.team?.value,
          mpr: defaultFilterForm?.rep_id?.value,
          group: defaultFilterForm?.group?.value,
          date_from: moment(defaultFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(defaultFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          table_type: 'rsm',
          team: filterForm?.team?.value,
          mpr: filterForm?.rep_id?.value,
          group: filterForm?.group?.value,
          date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallPerformanceDetailedView(request);
      const { code, data } = response.data;
      if (code === '200') {
        setRsmTable({ ...rsmTable, loading: false, rsmData: data });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleMprTable = async (clear = null) => {
    try {
      setMprTable({ ...mprTable, loading: true });
      let request;

      if (clear === 'reset') {
        request = {
          table_type: 'mpr',
          team: defaultFilterForm?.team?.value,
          mpr: defaultFilterForm?.rep_id?.value,
          group: defaultFilterForm?.group?.value,
          date_from: moment(defaultFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(defaultFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          table_type: 'mpr',
          team: filterForm?.team?.value,
          mpr: filterForm?.rep_id?.value,
          group: filterForm?.group?.value,
          date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallPerformanceDetailedView(request);
      const { code, data } = response.data;
      if (code === '200') {
        setMprTable({ ...mprTable, loading: false, mprData: data });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useImperativeHandle(ref, () => ({
    handleGenerateFilter: () => {
      // Chart
      handlePlottedMdsChart();
      handleFrequencyvsPlottedChart();
      handleCallRateChart();
      handleCallReachChart();
      handleCallConcentrationChart();
      // Table
      handleNsmTable();
      handleRsmTable();
      handleMprTable();
    },
    handleClearFilter: () => {
      // Chart
      handlePlottedMdsChart('reset');
      handleFrequencyvsPlottedChart('reset');
      handleCallRateChart('reset');
      handleCallReachChart('reset');
      handleCallConcentrationChart('reset');
      // Table
      handleNsmTable('reset');
      handleRsmTable('reset');
      handleMprTable('reset');
    },
  }));

  useEffect(() => {
    // Chart
    handlePlottedMdsChart();
    handleFrequencyvsPlottedChart();
    handleCallRateChart();
    handleCallReachChart();
    handleCallConcentrationChart();
    // Table
    handleNsmTable();
    handleRsmTable();
    handleMprTable();
  }, []);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={2.4}>
          <Box sx={{ border: '1px solid rgb(204, 201, 201)' }} p={2}>
            <Grid container>
              <Grid md={6}>
                <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>Plotted MDs</Typography>
              </Grid>
              <Grid md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant='text' onClick={() => handleToggleModal('plotted_mds')}>
                  View Breakdown
                </Button>
              </Grid>
            </Grid>
            <Grid container>
              <Grid md={6}>
                <DonutChart value={pmdsChart?.total_percentage} />
              </Grid>
              <Grid md={6} sx={{ justifyContent: 'center', alignContent: 'center' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }}>
                  MD Universe:{' '}
                  {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                    pmdsChart?.md_universe,
                  )}
                </Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }} mt={2}>
                  Tagged MDs:{' '}
                  {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                    pmdsChart?.tagged_mds,
                  )}
                </Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }} mt={2}>
                  Plotted MDs:{' '}
                  {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                    pmdsChart?.plotted_mds,
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={2.4}>
          <Box sx={{ border: '1px solid rgb(204, 201, 201)' }} p={2}>
            <Grid container>
              <Grid md={6}>
                <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>
                  Frequency vs Plotted
                </Typography>
              </Grid>
              <Grid md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant='text' onClick={() => handleToggleModal('fvp')}>
                  View Breakdown
                </Button>
              </Grid>
            </Grid>
            <Grid container>
              <Grid md={6}>
                <DonutChart value={fvpChart?.total_percentage} />
              </Grid>
              <Grid md={6} sx={{ justifyContent: 'center', alignContent: 'center' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }}>
                  Frequency:{' '}
                  {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                    fvpChart?.frequency,
                  )}
                </Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }} mt={2}>
                  Plotted:{' '}
                  {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                    fvpChart?.plotted,
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={2.4}>
          <Box sx={{ border: '1px solid rgb(204, 201, 201)' }} p={2}>
            <Grid container>
              <Grid md={6}>
                <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>Call Rate</Typography>
              </Grid>
              <Grid md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant='text' onClick={() => handleToggleModal('call_rate')}>
                  View Breakdown
                </Button>
              </Grid>
            </Grid>
            <Grid container>
              <Grid md={6}>
                <DonutChart value={callRateChart.total_percentage} />
              </Grid>
              <Grid container md={6} sx={{ justifyContent: 'center', alignContent: 'center' }}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  sx={{ justifyContent: 'center', textAlign: 'center' }}
                >
                  <Box sx={{ fontSize: '20px', fontWeight: 'bold', color: 'green' }}>
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      callRateChart?.actual,
                    )}
                  </Box>
                  <Box sx={{ fontSize: '15px', fontWeight: 'bold' }}>Actual</Box>
                </Grid>
                <Grid item xs={6} sm={6} md={6} lg={6} pt={1}>
                  <Box sx={{ fontSize: '15px', fontWeight: 'bold' }}>
                    Planned:{' '}
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      callRateChart?.planned,
                    )}
                  </Box>
                  <Box sx={{ fontSize: '15px', fontWeight: 'bold' }} pt={1}>
                    Advanced:{' '}
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      callRateChart?.advanced,
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6} sm={6} md={6} lg={6} pt={1}>
                  <Box sx={{ fontSize: '15px', fontWeight: 'bold' }}>
                    Retrieved:{' '}
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      callRateChart?.retrieved,
                    )}
                  </Box>
                  <Box sx={{ fontSize: '15px', fontWeight: 'bold' }} pt={1}>
                    Missed:{' '}
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      callRateChart?.missed,
                    )}
                  </Box>
                </Grid>
              </Grid>
              {/* <Grid container md={6} sx={{ justifyContent: 'center', alignContent: 'center' }}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  sx={{ justifyContent: 'center', textAlign: 'center' }}
                >
                  <Box sx={{ fontSize: '20px', fontWeight: 'bold', color: 'green' }}>
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      pvaChart?.actual,
                    )}
                  </Box>
                  <Box sx={{ fontSize: '15px', fontWeight: 'bold' }}>Actual</Box>
                </Grid>
                {/* <Grid
                  item
                  xs={6}
                  sm={6}
                  md={6}
                  lg={6}
                  sx={{ justifyContent: 'center', textAlign: 'center' }}
                  mt={2}
                >
                  <Box style={{ fontSize: '20px', fontWeight: 'bold', color: '#DC4C64' }}>
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      pvaChart?.overdue,
                    )}
                  </Box>
                  <Box sx={{ fontSize: '15px', fontWeight: 'bold' }}>Over Due</Box>
                </Grid> 
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={6}
                  lg={6}
                  sx={{ justifyContent: 'center', textAlign: 'center' }}
                  mt={2}
                >
                  <Box style={{ fontSize: '20px', fontWeight: 'bold', color: '#E4A11B' }}>
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      pvaChart?.due_soon,
                    )}
                  </Box>
                  <Box sx={{ fontSize: '15px', fontWeight: 'bold' }}>Due Soon</Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  sx={{ justifyContent: 'center', textAlign: 'center' }}
                  mt={2}
                >
                  <Box sx={{ fontSize: '20px', fontWeight: 'bold' }}>
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      pvaChart?.actual,
                    )}{' '}
                    /{' '}
                    {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                      pvaChart?.target,
                    )}
                  </Box>
                </Grid>
              </Grid> */}
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={2.4}>
          <Box sx={{ border: '1px solid rgb(204, 201, 201)' }} p={2}>
            <Grid container>
              <Grid md={6}>
                <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>Call Reach</Typography>
              </Grid>
              <Grid md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant='text' onClick={() => handleToggleModal('call_reach')}>
                  View Breakdown
                </Button>
              </Grid>
            </Grid>
            <Grid container>
              <Grid md={6}>
                <DonutChart value={callReachChart.total_percentage} />
              </Grid>
              <Grid md={6} sx={{ justifyContent: 'center', alignContent: 'center' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }}>
                  Target:{' '}
                  {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                    callReachChart?.target,
                  )}
                </Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }} mt={2}>
                  Reach:{' '}
                  {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                    callReachChart?.reach,
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={2.4}>
          <Box sx={{ border: '1px solid rgb(204, 201, 201)' }} p={2}>
            <Grid container>
              <Grid md={6}>
                <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>
                  Call Concentration
                </Typography>
              </Grid>
              <Grid md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant='text' onClick={() => handleToggleModal('call_concentration')}>
                  View Breakdown
                </Button>
              </Grid>
            </Grid>
            <Grid container>
              <Grid md={6}>
                <DonutChart value={callConcentrationChart.total_percentage} />
              </Grid>
              <Grid md={6} sx={{ justifyContent: 'center', alignContent: 'center' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }}>
                  Target:{' '}
                  {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                    callConcentrationChart?.target,
                  )}
                </Typography>
                <Typography sx={{ fontSize: '15px', fontWeight: 'bold' }} mt={2}>
                  Concentration:{' '}
                  {Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
                    callConcentrationChart?.concentration,
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6} lg={6} p={1}>
          <CallPerformanceTable
            name='nsm'
            height={400}
            loading={nsmTable?.loading}
            data={nsmTable?.nsmData}
          />
          <CallPerformanceTable
            name='rsm'
            height={400}
            loading={rsmTable?.loading}
            data={rsmTable?.rsmData}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} p={1}>
          <CallPerformanceTable
            name='mpr'
            height={800}
            loading={mprTable?.loading}
            data={mprTable?.mprData}
          />
        </Grid>
      </Grid>
    </Box>
  );
});

export default CallPerformanceTab;
